import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, Plus, Trash, Users, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FiltersEditor, RecipientCount } from "../components/marketing/SegmentBuilder";

const TABS = [["clienti", "Clienti"], ["tags", "Tags"], ["segmenti", "Segmenti"]];
const inputCls = "h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";

export default function Clienti() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSala = user?.role === "sala";
  const [tab, setTab] = useState("clienti");
  const [q, setQ] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [minVisits, setMinVisits] = useState("");
  const [sortVisits, setSortVisits] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState("");
  const [segments, setSegments] = useState([]);
  const [editSeg, setEditSeg] = useState(null);
  const [segName, setSegName] = useState("");
  const [preview, setPreview] = useState(null);
  const [includeNoConsent, setIncludeNoConsent] = useState(false);
  const [members, setMembers] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkTag, setBulkTag] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const loadTags = useCallback(() => api.get("/tags").then((r) => setTags(r.data)).catch(() => {}), []);
  const loadSegments = useCallback(() => api.get("/marketing/segments").then((r) => setSegments(r.data)).catch(() => {}), []);

  useEffect(() => { loadTags(); loadSegments(); }, [loadTags, loadSegments]);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (tagFilter) params.set("tag", tagFilter);
      if (minVisits) params.set("min_visits", minVisits);
      if (sortVisits) params.set("sort", "visits");
      api.get(`/customers${params.size ? `?${params}` : ""}`)
        .then((r) => setList(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, tagFilter, minVisits, sortVisits, reloadKey]);

  const createTag = async (e) => {
    e.preventDefault();
    const name = newTagName.trim();
    if (!name) return;
    try {
      await api.post("/tags", { name });
      setNewTagName("");
      loadTags();
      toast.success(`Tag "${name}" creato`);
    } catch (err) { toast.error(apiError(err)); }
  };

  const deleteTag = async (t) => {
    try {
      await api.delete(`/tags/${t.id}`);
      loadTags();
      toast.success(`Tag "${t.name}" eliminato`);
    } catch (err) { toast.error(apiError(err)); }
  };

  const viewTagContacts = (t) => {
    setTagFilter(t.name);
    setTab("clienti");
  };

  const runPreview = async () => {
    try {
      const r = await api.post("/marketing/segments/preview", { combinator: editSeg.combinator, filters: editSeg.filters, include_no_consent: includeNoConsent });
      setPreview(r.data);
    } catch (err) { toast.error(apiError(err)); setPreview({ error: true }); }
  };

  const viewMembers = async (combinator, filters, name) => {
    try {
      const r = await api.post("/marketing/segments/members", { combinator, filters });
      setMembers({ name, list: r.data });
      setTimeout(() => document.querySelector('[data-testid="segment-members"]')
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 150);
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveSegment = async () => {
    if (!segName.trim()) { toast.error("Dai un nome al segmento"); return; }
    try {
      await api.post("/marketing/segments", { name: segName.trim(), combinator: editSeg.combinator, filters: editSeg.filters });
      toast.success("Segmento salvato — lo trovi anche nel wizard Campagne");
      setEditSeg(null);
      setSegName("");
      setPreview(null);
      loadSegments();
    } catch (err) { toast.error(apiError(err)); }
  };

  const deleteSegment = async (s) => {
    try {
      await api.delete(`/marketing/segments/${s.id}`);
      loadSegments();
      toast.success("Segmento eliminato");
    } catch (err) { toast.error(apiError(err)); }
  };

  const customerRow = (c) => (
    <div key={c.id} className="w-full flex items-center hover:bg-zinc-50 transition-colors" data-testid={`customer-row-${c.id}`}>
      <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSel(c.id)}
        data-testid={`customer-sel-${c.id}`} className="w-4 h-4 accent-zinc-900 shrink-0 ml-3" />
      <button onClick={() => navigate(`/clienti/${c.id}`)} className="flex-1 flex items-center gap-3 px-3 py-3 text-left min-w-0">
        <div className="w-10 h-10 rounded-full bg-zinc-900 text-white flex items-center justify-center font-display font-bold text-sm shrink-0">
          {(c.first_name?.[0] || "?")}{(c.last_name?.[0] || "")}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-zinc-900 truncate">{c.first_name} {c.last_name}</div>
          <div className="text-xs text-zinc-500 truncate">{c.phone || c.email || "—"}</div>
          {(c.tags || []).length > 0 && (
            <div className="flex gap-1 mt-1 flex-wrap">
              {c.tags.slice(0, 3).map((t) => (
                <span key={t} className="text-[10px] font-semibold bg-cyan-50 text-cyan-800 border border-cyan-200 rounded-full px-2 py-0.5">{t}</span>
              ))}
              {c.tags.length > 3 && <span className="text-[10px] text-zinc-400">+{c.tags.length - 3}</span>}
            </div>
          )}
        </div>
        {c.visits !== undefined && (
          <div className="text-right text-xs text-zinc-500 shrink-0">
            <div><strong className="text-zinc-900">{c.visits}</strong> visite</div>
            {c.no_shows > 0 && <div className="text-rose-600">{c.no_shows} no-show</div>}
          </div>
        )}
      </button>
    </div>
  );

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSelected((s) => (s.length === list.length && list.length > 0) ? [] : list.map((c) => c.id));
  const bulkAction = async (action) => {
    try {
      const r = await api.post("/customers/bulk", { ids: selected, action, tag: bulkTag || undefined });
      toast.success(`${r.data.modified} clienti aggiornati`);
      setSelected([]); setBulkTag(""); setReloadKey((k) => k + 1);
    } catch (e) { toast.error(apiError(e)); }
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Eliminare definitivamente i ${selected.length} clienti selezionati? Le prenotazioni storiche restano.`)) return;
    try {
      const r = await api.post("/customers/bulk", { ids: selected, action: "delete" });
      toast.success(`${r.data.modified} clienti eliminati`);
      setSelected([]); setReloadKey((k) => k + 1);
    } catch (e) { toast.error(apiError(e)); }
  };
  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (tagFilter) params.set("tag", tagFilter);
      if (minVisits) params.set("min_visits", minVisits);
      const r = await api.get(`/customers/export.csv${params.size ? `?${params}` : ""}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      a.download = "clienti.csv"; a.click(); URL.revokeObjectURL(a.href);
      toast.success("Export CSV scaricato");
    } catch (e) { toast.error(apiError(e)); }
  };
  const applySegment = (segId) => {
    if (!segId) { setMembers(null); return; }
    const s = segments.find((x) => x.id === segId);
    if (s) viewMembers(s.combinator, s.filters, s.name);
  };

  const membersPanel = members && (
    <div className="bg-white border border-zinc-200 rounded-lg" data-testid="segment-members">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
        <span className="text-sm font-semibold text-zinc-900">{members.name}: {members.list.length} contatti</span>
        <button onClick={() => setMembers(null)} data-testid="segment-members-close" className="p-1.5 text-zinc-500 hover:text-zinc-900"><X size={15} /></button>
      </div>
      <div className="divide-y divide-zinc-100 max-h-96 overflow-y-auto">
        {members.list.length === 0 && <div className="py-6 text-center text-sm text-zinc-400">Nessun contatto nel segmento</div>}
        {members.list.map(customerRow)}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="clienti-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Clienti</h1>
        {tab === "segmenti" && !editSeg && (
          <button onClick={() => { setEditSeg({ combinator: "and", filters: [] }); setSegName(""); setPreview(null); setMembers(null); }}
            data-testid="new-segment-btn"
            className="h-10 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
            <Plus size={14} weight="bold" /> Nuovo segmento
          </button>
        )}
      </div>

      {!isSala && (
        <div className="flex gap-1 border-b border-zinc-200" data-testid="clienti-tabs">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`clienti-tab-${k}`}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${tab === k ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}>
              {l}
            </button>
          ))}
        </div>
      )}

      {tab === "clienti" && (
        <>
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, telefono o email..." data-testid="clienti-search"
                className="w-full h-11 pl-9 pr-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900" />
            </div>
            {tagFilter && (
              <span className="flex items-center gap-1.5 text-xs font-semibold bg-cyan-50 text-cyan-800 border border-cyan-200 rounded-full px-3 py-2" data-testid="clienti-tag-filter">
                Tag: {tagFilter}
                <button onClick={() => setTagFilter("")} data-testid="clienti-tag-filter-clear" className="hover:text-cyan-950"><X size={12} weight="bold" /></button>
              </span>
            )}
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <select onChange={(e) => applySegment(e.target.value)} defaultValue="" data-testid="clienti-segment"
              className="h-9 px-2.5 rounded-md border border-zinc-300 text-sm bg-white max-w-48">
              <option value="">Segmento: tutti</option>
              {segments.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} data-testid="clienti-tag-select"
              className="h-9 px-2.5 rounded-md border border-zinc-300 text-sm bg-white max-w-44">
              <option value="">Tag: tutti</option>
              {tags.map((t) => <option key={t.id || t.name} value={t.name}>{t.name}</option>)}
            </select>
            <input type="number" min="0" value={minVisits} onChange={(e) => setMinVisits(e.target.value)}
              placeholder="Visite minime" data-testid="clienti-min-visits"
              className="h-9 w-32 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900" />
            <button onClick={() => setSortVisits(!sortVisits)} data-testid="clienti-sort-visits"
              className={`h-9 px-3 rounded-md border text-xs font-semibold transition ${sortVisits ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400"}`}>
              Più visite prima
            </button>
            <button onClick={exportCsv} data-testid="clienti-export"
              className="h-9 px-3 rounded-md border border-zinc-300 bg-white text-xs font-semibold text-zinc-600 hover:border-zinc-400">
              Esporta CSV
            </button>
            <label className="flex items-center gap-1.5 text-xs text-zinc-500 ml-auto">
              <input type="checkbox" checked={list.length > 0 && selected.length === list.length} onChange={toggleAll}
                data-testid="clienti-sel-all" className="w-4 h-4 accent-zinc-900" /> Tutti
            </label>
          </div>
          {selected.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap bg-zinc-900 text-white rounded-lg px-3 py-2" data-testid="bulk-bar">
              <span className="text-xs font-bold" data-testid="bulk-count">{selected.length} selezionati</span>
              <select value={bulkTag} onChange={(e) => setBulkTag(e.target.value)} data-testid="bulk-tag"
                className="h-8 px-2 rounded-md border border-zinc-600 text-xs bg-zinc-800 text-white">
                <option value="">Scegli tag...</option>
                {tags.map((t) => <option key={t.id || t.name} value={t.name}>{t.name}</option>)}
              </select>
              <button onClick={() => bulkAction("add_tag")} disabled={!bulkTag} data-testid="bulk-add-tag"
                className="h-8 px-3 rounded-md bg-emerald-600 text-xs font-bold disabled:opacity-40">+ Tag</button>
              <button onClick={() => bulkAction("remove_tag")} disabled={!bulkTag} data-testid="bulk-remove-tag"
                className="h-8 px-3 rounded-md bg-amber-600 text-xs font-bold disabled:opacity-40">− Tag</button>
              <button onClick={bulkDelete} data-testid="bulk-delete"
                className="h-8 px-3 rounded-md bg-rose-600 text-xs font-bold">Elimina</button>
              <button onClick={() => setSelected([])} data-testid="bulk-clear" className="p-1.5 text-zinc-400 hover:text-white ml-auto"><X size={15} /></button>
            </div>
          )}
          <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="clienti-list">
            {loading && <div className="py-8 text-center text-sm text-zinc-400">Caricamento...</div>}
            {!loading && list.length === 0 && <div className="py-8 text-center text-sm text-zinc-400">Nessun cliente trovato</div>}
            {list.map(customerRow)}
          </div>
          {membersPanel}
        </>
      )}

      {tab === "tags" && (
        <div className="space-y-3" data-testid="tags-tab">
          <form onSubmit={createTag} className="flex gap-2">
            <input value={newTagName} onChange={(e) => setNewTagName(e.target.value.toUpperCase())} placeholder="Nome nuovo tag (es. VIP-2026)" data-testid="tag-new-input"
              className={`flex-1 ${inputCls}`} />
            <button type="submit" data-testid="tag-new-save"
              className="h-10 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
              <Plus size={14} weight="bold" /> Aggiungi tag
            </button>
          </form>
          <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="tags-list">
            {tags.length === 0 && <div className="py-8 text-center text-sm text-zinc-400">Nessun tag. Creane uno per associarlo a coupon, moduli web e segmenti.</div>}
            {tags.map((t) => (
              <div key={t.id} className="flex items-center gap-3 px-4 py-3" data-testid={`tag-row-${t.id}`}>
                <span className="flex-1 text-sm font-semibold text-zinc-900 font-mono">{t.name}</span>
                <span className="text-xs text-zinc-500" data-testid={`tag-usage-${t.id}`}><strong className="text-zinc-900">{t.usage}</strong> contatti</span>
                <button onClick={() => viewTagContacts(t)} data-testid={`tag-view-${t.id}`}
                  className="h-8 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 hover:border-zinc-400">
                  Vedi contatti
                </button>
                <button onClick={() => deleteTag(t)} data-testid={`tag-del-${t.id}`}
                  className="p-2 text-zinc-400 hover:text-rose-600"><Trash size={15} /></button>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-zinc-400">I tag si associano ai moduli: Coupon (tab Impostazioni: download/utilizzo/non utilizzato), Moduli Web (tag al prenotato), filtri dei Segmenti e ricerca clienti.</p>
        </div>
      )}

      {tab === "segmenti" && (
        <div className="space-y-3" data-testid="segmenti-tab">
          {!editSeg ? (
            <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="segments-list">
              {segments.length === 0 && <div className="py-8 text-center text-sm text-zinc-400">Nessun segmento salvato. Clicca "Nuovo segmento" per definirne uno.</div>}
              {segments.map((s) => (
                <div key={s.id} className="flex items-center gap-3 px-4 py-3 flex-wrap" data-testid={`segment-row-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-zinc-900 truncate">{s.name}</div>
                    <div className="text-xs text-zinc-500">{(s.filters || []).length} condizioni · {s.combinator === "or" ? "almeno una" : "tutte"}</div>
                  </div>
                  <button onClick={() => viewMembers(s.combinator, s.filters, s.name)} data-testid={`segment-view-${s.id}`}
                    className="h-8 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 hover:border-zinc-400 flex items-center gap-1.5">
                    <Users size={13} /> Vedi contatti
                  </button>
                  <button onClick={() => deleteSegment(s)} data-testid={`segment-del-${s.id}`}
                    className="p-2 text-zinc-400 hover:text-rose-600"><Trash size={15} /></button>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-3" data-testid="segment-editor">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold text-zinc-900">Definisci segmento</h3>
                <button onClick={() => { setEditSeg(null); setPreview(null); }} data-testid="segment-cancel" className="p-1.5 text-zinc-500 hover:text-zinc-900"><X size={16} /></button>
              </div>
              <input value={segName} onChange={(e) => setSegName(e.target.value)} placeholder="Nome segmento (es. Clienti inattivi da 60 giorni)" data-testid="segment-name"
                className={`w-full ${inputCls}`} />
              <FiltersEditor combinator={editSeg.combinator} filters={editSeg.filters}
                onChange={(v) => { setEditSeg(v); setPreview(null); }} />
              <div className="flex items-center gap-2 flex-wrap" data-testid="segment-consent-toggle">
                <span className="text-xs font-semibold text-zinc-500">Conteggio destinatari:</span>
                {[["only", "Solo consenso marketing"], ["all", "Tutti, anche senza consenso"]].map(([k, l]) => (
                  <button type="button" key={k} onClick={() => { setIncludeNoConsent(k === "all"); setPreview(null); }}
                    data-testid={`segment-consent-${k}`}
                    className={`h-8 px-3 rounded-full text-[11px] font-bold border transition ${(includeNoConsent ? "all" : "only") === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300"}`}>
                    {l}
                  </button>
                ))}
              </div>
              {includeNoConsent && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="segment-consent-warn">
                  Inclusi anche i contatti senza consenso marketing. Base giuridica (es. soft opt-in per email a clienti reali) sotto la tua responsabilità. Per WhatsApp l'invio a non consenzienti rischia la sospensione del numero da parte di Meta.
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <button onClick={runPreview} data-testid="segment-preview-btn"
                  className="h-10 px-4 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 hover:border-zinc-400">
                  Cerca
                </button>
                <button onClick={() => viewMembers(editSeg.combinator, editSeg.filters, segName || "Anteprima")} data-testid="segment-members-btn"
                  className="h-10 px-4 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 hover:border-zinc-400">
                  Vedi contatti
                </button>
                <button onClick={saveSegment} data-testid="segment-save-btn"
                  className="h-10 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold">
                  Salva segmento
                </button>
              </div>
              <RecipientCount preview={preview} />
              {membersPanel}
            </div>
          )}
          {!editSeg && membersPanel}
        </div>
      )}
    </div>
  );
}
